import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FINANCIAL_REQUEST_TIMEOUT } from 'shared-ui';
import { SKIP_AUTH_REFRESH } from '../../interceptors/auth-expired.interceptor';

export type StartSubscriptionCheckoutRequest = {
  planCode: string;
  orgId?: string | null;
  email?: string | null;
};

export type ChangeSubscriptionPlanRequest = {
  planCode: string;
  orgId?: string | null;
  quoteToken?: string;
  prorationDate?: number;
  expectedAmountDueNow?: number;
  expectedCurrency?: string;
  // Required when the preview listed overLimits; the server refuses the change without it.
  acknowledgeOverLimits?: boolean;
};

export type PlanChangeKind =
  | 'UPGRADE_CHARGED_NOW'
  | 'DOWNGRADE_SCHEDULED'
  | 'SWITCH_NOW_CREDIT'
  | 'SWITCH_NOW_NO_CHARGE';

export type PlanChangeOverLimit = {
  resource: 'ORGANIZATIONS' | 'STAFF' | 'PROPERTIES' | 'UNITS' | string;
  used: number;
  newLimit: number;
  // Workspaces: must be brought within the new limit before switching (the change is refused until then).
  blocking?: boolean;
};

export type PlanChangeStatus = 'PENDING' | 'REQUIRES_ACTION' | 'SUCCEEDED' | 'FAILED' | 'EXPIRED';

// Outcome of change-plan and change-plan/complete. clientSecret is only set while 3D Secure is needed.
export type PlanChangeResult = {
  attemptId: string;
  status: PlanChangeStatus;
  targetPlanCode?: string | null;
  clientSecret?: string | null;
  failureCode?: string | null;
  failureReason?: string | null;
  amountDue?: number | null;
  currency?: string | null;
};

export type CompletePlanChangeRequest = {
  attemptId?: string | null;
  orgId?: string | null;
  // 3D Secure failed or was closed in the browser: the server ends the attempt unless Stripe shows it was paid.
  abandon?: boolean;
  reason?: string | null;
};

export type SubscriptionPaymentMethod = {
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  default?: boolean;
};

export type SubscriptionPlanChangePreview = {
  quoteToken?: string;
  currentPlanName: string;
  targetPlanName: string;
  planCode: string;
  billingInterval: string;
  scheduled: boolean;
  effectiveAt: string;
  resumesAutoRenew: boolean;
  // Older servers omit these; the review then falls back to the amount due.
  changeKind?: PlanChangeKind | null;
  creditAmount?: number | null;
  intervalChange?: boolean | null;
  currentBillingInterval?: string | null;
  overLimits?: PlanChangeOverLimit[] | null;
  blocked?: boolean | null;
  blockedReason?: string | null;
  billing: {
    currency: string;
    amountDueNow: number;
    invoiceTotal: number;
    recurringAmount: number;
    nextBillingAt: string | null;
    prorationDate: number;
    lines: Array<{ description: string; amount: number }>;
    billedNow?: boolean;
    creditAmount?: number;
  };
};

export type StartSubscriptionCheckoutResponse = {
  subscriptionId: string;
  provider: string;
  providerCustomerId: string;
  providerSubscriptionId: string;
  providerSubscriptionItemId: string;
  clientSecret: string;
  clientSecretType: 'SETUP_INTENT' | 'PAYMENT_INTENT';
  planCode: string;
  currency: string;
  amount: string;
  trialDays?: number | null;
  trialEndsAt?: string | null;
  status: string;
  orgId?: string | null;
  accountType?: 'LANDLORD' | 'PROPERTY_MANAGER' | null;
  createWorkspaceMode?: boolean | null;
  returnUrl?: string | null;
  // Set by the plan picker so the payment summary can show the real plan name and billing interval.
  planDisplayName?: string | null;
  billingInterval?: string | null;
};

export type FinalizeTrialPaymentMethodRequest = {
  externalSubscriptionId: string;
  paymentIntentId: string;
};
export type SubscriptionPlan = {
  id: string;
  planCode: string;
  accountType: string;
  displayName: string;
  description: string;
  billingInterval: string;
  priceAmount: number;
  currency: string;
  features: string[];

  trialEnabled?: boolean;
  trialDays?: number | null;
  cardRequiredForTrial?: boolean;

  externalProvider?: string | null;
  externalProductId?: string | null;
  externalPriceId?: string | null;

  maxOrganizations: number;
  maxPropertiesPerOrg: number;
  maxStaffPerOrg: number;
  maxUnitsPerOrg: number;
  unlimitedOrganizations: boolean;
  unlimitedProperties: boolean;
  unlimitedStaff: boolean;
  unlimitedUnits: boolean;
  recommended: boolean;
  active: boolean;
  sortOrder: number;
  version: number;
};
export type ActivateTrialRequest = {
  planCode: 'FREE_TRIAL';
  orgId?: string | null;
};

export type SubscriptionStatusResponse = {
  active: boolean;
  // Whether change-plan accepts this subscription (active or trialing in Stripe); otherwise the picker offers checkout.
  planChangeAvailable?: boolean | null;
  // Plan features, and whether they're enforced; until enforced every feature counts as included.
  features?: string[] | null;
  featureEnforcement?: boolean | null;
  planCode?: string | null;
  status?: string | null;
  billingInterval?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  trialEndsAt?: string | null;
  pendingPlanCode?: string | null;
  pendingExternalPriceId?: string | null;
  planChangeEffectiveAt?: string | null;
  subscriptionState?:
    | 'ACTIVE'
    | 'TRIALING'
    | 'EXPIRED'
    | 'TRIAL_EXPIRED'
    | 'CANCELED'
    | 'PAST_DUE'
    | 'INCOMPLETE'
    | 'NONE'
    | string
    | null;
  message?: string | null;
  nextAction?: string | null;
  organizationsUsed?: number | null;
  staffUsed?: number | null;
  propertiesUsed?: number | null;
  unitsUsed?: number | null;
  maxOrganizations?: number | null;
  maxStaffPerOrg?: number | null;
  maxPropertiesPerOrg?: number | null;
  maxUnitsPerOrg?: number | null;
  unlimitedOrganizations?: boolean | null;
  unlimitedStaff?: boolean | null;
  unlimitedProperties?: boolean | null;
  unlimitedUnits?: boolean | null;
  canCreateOrganization?: boolean | null;
  canAddStaff?: boolean | null;
  canAddProperty?: boolean | null;
  canAddUnit?: boolean | null;
  planChange?: PlanChangeResult | null;
};

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiGatewayUrl}/subscriptions/api/v1`;

  activateTrial(payload: ActivateTrialRequest, options: { skipAuthRefresh?: boolean } = {}): Observable<any> {
    return this.http.post(`${this.base}/internal/subscriptions/start-trial`, payload, {
      context: options.skipAuthRefresh ? new HttpContext().set(SKIP_AUTH_REFRESH, true) : undefined,
      withCredentials: true,
    });
  }

  startCheckout(
    payload: StartSubscriptionCheckoutRequest,
    options: { skipAuthRefresh?: boolean } = {},
  ): Observable<StartSubscriptionCheckoutResponse> {
    return this.http.post<StartSubscriptionCheckoutResponse>(
      `${this.base}/internal/subscriptions/checkout`,
      payload,
      {
        context: options.skipAuthRefresh ? new HttpContext().set(SKIP_AUTH_REFRESH, true) : undefined,
        withCredentials: true,
      },
    );
  }

  previewPlanChange(payload: ChangeSubscriptionPlanRequest): Observable<SubscriptionPlanChangePreview> {
    return this.http.post<SubscriptionPlanChangePreview>(
      `${this.base}/internal/subscriptions/change-plan/preview`, payload, { withCredentials: true },
    );
  }

  changePlan(
    payload: ChangeSubscriptionPlanRequest,
    options: { skipAuthRefresh?: boolean } = {},
  ): Observable<SubscriptionStatusResponse> {
    return this.http.post<SubscriptionStatusResponse>(
      `${this.base}/internal/subscriptions/change-plan`,
      payload,
      {
        context: new HttpContext().set(SKIP_AUTH_REFRESH, !!options.skipAuthRefresh).set(FINANCIAL_REQUEST_TIMEOUT, 20000),
        withCredentials: true,
      },
    );
  }

  completePlanChange(payload: CompletePlanChangeRequest): Observable<SubscriptionStatusResponse> {
    return this.http.post<SubscriptionStatusResponse>(
      `${this.base}/internal/subscriptions/change-plan/complete`, payload, { withCredentials: true },
    );
  }

  getPaymentMethod(): Observable<SubscriptionPaymentMethod> {
    return this.http.get<SubscriptionPaymentMethod>(`${this.base}/internal/subscriptions/payment-method`, {
      withCredentials: true,
    });
  }

  createBillingPortal(payload: { returnUrl: string; flow?: 'payment_method_update' }): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.base}/internal/subscriptions/billing-portal`, payload, {
      withCredentials: true,
    });
  }

  finalizeTrialPaymentMethod(payload: FinalizeTrialPaymentMethodRequest): Observable<void> {
    return this.http.post<void>(`${this.base}/internal/subscriptions/trial-payment-method/finalize`, payload, {
      withCredentials: true,
    });
  }

  getPlans(accountType = 'LANDLORD'): Observable<SubscriptionPlan[]> {
    return this.http.get<SubscriptionPlan[]>(`${this.base}/public/plans`, {
      params: { accountType },
    });
  }
  getMySubscriptionStatus(options: { skipAuthRefresh?: boolean } = {}): Observable<SubscriptionStatusResponse> {
    return this.http.get<SubscriptionStatusResponse>(`${this.base}/internal/subscriptions/me`, {
      context: options.skipAuthRefresh ? new HttpContext().set(SKIP_AUTH_REFRESH, true) : undefined,
      withCredentials: true,
    });
  }
}
